import { useEffect, useState } from "react";
import { Rating } from "@smastrom/react-rating";
import "@smastrom/react-rating/style.css";
import { ReviewService } from "../../services";
import { useMyReceivedReviews } from "../../hooks/queries";
import { usePersistStore, useStore } from "../../store";
import Avatar from "../../components/Avatar";
import Button from "../../controls/Button";
import { Utils } from "../../utils";

const PAGE_SIZE = 10;

function MyReviews() {
  const { token } = usePersistStore((s: any) => s);
  const { setErrorMessage, setSuccessMessage, setIsLoading } = useStore(
    (s: any) => s
  );
  const [page, setPage] = useState(0);
  const [responseDrafts, setResponseDrafts] = useState<Record<string, string>>(
    {}
  );

  const { data, isLoading, error, refetch } = useMyReceivedReviews(
    token,
    page,
    PAGE_SIZE
  );
  const list: any[] = data?.list || [];
  const count: number = data?.count || 0;
  const isReady = !isLoading;

  useEffect(() => {
    if (error) setErrorMessage((error as Error).message);
  }, [error, setErrorMessage]);

  const submitResponse = async (reviewId: string) => {
    const text = (responseDrafts[reviewId] || "").trim();
    if (!text) {
      setErrorMessage("Please write a response.");
      return;
    }
    setIsLoading(true);
    const result = await ReviewService.submitSellerResponse(token, reviewId, text);
    setIsLoading(false);
    if (result.error) {
      setErrorMessage(result.error);
      return;
    }
    setSuccessMessage(
      "Your response has been submitted and is awaiting moderation."
    );
    setResponseDrafts({ ...responseDrafts, [reviewId]: "" });
    refetch();
  };

  const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));

  return (
    <div className="bg-pink p-4">
      <div className="row">
        <div className="col-12">
          <div className="chartCard py-4">
            <div className="tableCardHeader px-4">
              <div className="tableCardHeader__titleBlock">
                <h2 className="tableCardHeader__title m-0">Reviews about me</h2>
                <p className="tableCardHeader__subtitle m-0">
                  {!isReady
                    ? "Loading your reviews…"
                    : count === 0
                    ? "No approved reviews yet"
                    : `${count} approved review${count === 1 ? "" : "s"}`}
                </p>
              </div>
            </div>

            <div className="reviewList">
              {!isReady &&
                Array.from({ length: 3 }).map((_, i) => (
                  <div className="reviewCard reviewCard--skeleton" key={i}>
                    <div className="reviewCard__head">
                      <div className="reviewCard__avatar reviewCard__skel reviewCard__skel--circle" />
                      <div className="reviewCard__skelStack">
                        <div className="reviewCard__skel reviewCard__skel--line reviewCard__skel--short" />
                        <div className="reviewCard__skel reviewCard__skel--line reviewCard__skel--tiny" />
                      </div>
                    </div>
                    <div className="reviewCard__skel reviewCard__skel--line" />
                    <div className="reviewCard__skel reviewCard__skel--line" />
                  </div>
                ))}

              {isReady && list.length === 0 && (
                <div className="reviewEmpty">
                  <i className="bi bi-chat-left-text reviewEmpty__icon"></i>
                  <h3 className="reviewEmpty__title">No reviews yet</h3>
                  <p className="reviewEmpty__text">
                    Once buyers approve their reviews, they'll show up here.
                  </p>
                </div>
              )}

              {isReady &&
                list.map((r: any) => (
                  <div key={r._id} className="reviewCard">
                    <div className="reviewCard__head">
                      <Avatar
                        src={r?.buyerId?.fileUrl}
                        name={r?.buyerId?.name}
                        className="reviewCard__avatar"
                      />
                      <div className="reviewCard__headBody">
                        <div className="reviewCard__buyer">
                          <strong className="reviewCard__name">
                            {r?.buyerId?.name}
                          </strong>
                          <Rating
                            style={{ maxWidth: 90 }}
                            value={r.rating}
                            readOnly
                          />
                        </div>
                        <div className="reviewCard__meta">
                          <span className="reviewCard__date">
                            {Utils.formatDate(r.createdAt)}
                          </span>
                          {r?.adId?.title && (
                            <>
                              <span className="reviewCard__dot">·</span>
                              <span className="reviewCard__about">
                                About {r.adId.title}
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>

                    <p className="reviewCard__text">{r.text}</p>

                    {r?.sellerResponse?.text ? (
                      <div className="reviewResponse">
                        <div className="reviewResponse__head">
                          <i className="bi bi-reply-fill reviewResponse__icon"></i>
                          <span className="reviewResponse__label">
                            Your response
                          </span>
                          <span
                            className={`reviewResponse__status reviewResponse__status--${r.sellerResponse.status}`}
                          >
                            {r.sellerResponse.status}
                          </span>
                          <span className="reviewResponse__date">
                            {Utils.formatDate(r.sellerResponse.respondedAt)}
                          </span>
                        </div>
                        <p className="reviewResponse__text">
                          {r.sellerResponse.text}
                        </p>
                      </div>
                    ) : (
                      <div className="reviewReply formMain">
                        <label className="reviewReply__label">
                          Write a response
                        </label>
                        <textarea
                          className="form-control reviewReply__input"
                          rows={3}
                          placeholder="Share your perspective with future buyers…"
                          value={responseDrafts[r._id] || ""}
                          maxLength={2000}
                          onChange={(e) =>
                            setResponseDrafts({
                              ...responseDrafts,
                              [r._id]: e.target.value,
                            })
                          }
                        />
                        <div className="reviewReply__actions">
                          <Button
                            title="Submit response"
                            onClick={() => submitResponse(r._id)}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                ))}
            </div>

            {isReady && count > 0 && totalPages > 1 && (
              <div className="paginationMain">
                <div className="paginationMain__info">
                  <span className="paginationMain__summary">
                    Page {page + 1} of {totalPages} · {count} total
                  </span>
                </div>
                <div className="pagination-container">
                  <div
                    className={`pagination-item ${page === 0 ? "" : ""}`}
                    {...(page === 0 ? { "aria-disabled": true } : {})}
                    onClick={() => page > 0 && setPage(page - 1)}
                    style={{ opacity: page === 0 ? 0.4 : undefined, pointerEvents: page === 0 ? "none" : undefined }}
                  >
                    <i className="bi bi-chevron-left"></i>
                  </div>
                  <div className="paginationMain__pageIndicator">
                    {page + 1} of {totalPages}
                  </div>
                  <div
                    className="pagination-item"
                    onClick={() => page + 1 < totalPages && setPage(page + 1)}
                    style={{
                      opacity: page + 1 >= totalPages ? 0.4 : undefined,
                      pointerEvents:
                        page + 1 >= totalPages ? "none" : undefined,
                    }}
                  >
                    <i className="bi bi-chevron-right"></i>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default MyReviews;
